import React, { useMemo, useState } from "react";
import { Table, ConfigProvider, theme as antdTheme } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Sun, Moon, Funnel, FolderOutput, File } from "lucide-react";
import { mixformatCurrency } from "services/mixin";
import Layout from '../../Layout';
import { Helmet } from "react-helmet-async";
import { ModalCustomFilter } from "./components/ModalCustomFilter";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface PitRow {
    key: string;
    employeeCode: string; // Mã nhân sự
    fullName: string; // Họ và tên
    taxCode: string | null; // Mã số thuế
    subject: string | null; // Đối tượng
    company: string | null; // Công ty
    contractType: string | null; // Loại hợp đồng
    taxRate: string | null; // Thuế suất
    authorized: boolean; // Ủy quyền quyết toán thuế
    taxableIncome: number | null; // Tổng thu nhập chịu thuế
    dependents: number | null; // Số người phụ thuộc
    dependentDeduction: number | null; // Mức miễn giảm
    selfDeduction: number | null; // Bản thân
    insuranceDeduction: number | null; // Bảo hiểm
    totalDeduction: number | null; // Tổng giảm trừ
    finalTaxableIncome: number | null; // Tổng thu nhập tính thuế
    taxPayable: number | null; // Thuế TNCN phải nộp
    taxWithheld: number | null; // Thuế TNCN đã khấu trừ
    taxRemaining: number | null; // Thuế TNCN còn phải nộp
    taxOverpaid: number | null; // Thuế TNCN đã nộp thừa
}

/* ------------------------------------------------------------------ */
/*  Demo data (transcribed from the source table)                     */
/* ------------------------------------------------------------------ */
const R = (
    employeeCode: string,
    fullName: string,
    taxCode: string | null,
    selfDeduction: number,
    taxWithheld: number,
    taxRemaining: number | null
): PitRow => ({
    key: employeeCode,
    employeeCode,
    fullName,
    taxCode,
    subject: null,
    company: null,
    contractType: null,
    taxRate: null,
    authorized: true,
    taxableIncome: null,
    dependents: null,
    dependentDeduction: 4_400_000,
    selfDeduction,
    insuranceDeduction: null,
    totalDeduction: selfDeduction,
    finalTaxableIncome: null,
    taxPayable: null,
    taxWithheld,
    taxRemaining,
    taxOverpaid: taxRemaining ? null : taxWithheld,
});

const DATA: PitRow[] = [
    R("00063", "Nguyễn Thị Điệp", null, 22_000_000, 253_905, null),
    R("00067", "Trang Khải Phú", null, 22_000_000, 253_905, null),
    R("00068", "Nguyễn Thu Trang", "1942421288", 22_000_000, 253_905, null),
    R("00072", "Phạm Minh Bạch", null, 33_000_000, 366_405, null),
    R("00081", "Nguyễn Thành Vinh", null, 22_000_000, 747_500, null),
    R("00085", "Nguyễn Quốc Trung", "1942421286", 22_000_000, 253_905, null),
    R("00090", "Nguyễn Minh Luân", "1942421285", 22_000_000, 253_905, null),
    R("00093", "Đàm Cảnh Dự", null, 33_000_000, 966_405, null),
    R("00106", "Nguyễn Tuyết Mai", null, 22_000_000, -1_500, 1_500),
    R("0011", "Hải Triều", null, 22_000_000, 513_500, null),
    R("00116", "Nguyễn Vũ Tùng", null, 33_000_000, 816_405, null),
    R("0012", "Trần Văn Long", null, 22_000_000, 548_500, null),
    R("0013", "Trịnh Bá Minh", null, 22_000_000, 648_500, null),
    R("0018", "Trường Giang", null, 22_000_000, -1_500, 1_500),
    R("00305", "Nguyễn Bá Thọ", "1942421234", 22_000_000, 253_905, null),
    R("00310", "Vũ Ngọc Sơn", "1942421233", 22_000_000, 243_905, null),
    R("00347", "Trần Văn Nhật", null, 22_000_000, 253_905, null),
    R("00355", "Nguyễn Thị Thu Tình", null, 22_000_000, 441_405, null),
    R("00356", "Vũ Thị Thu Hằng", "1942421267", 22_000_000, 253_905, null),
    R("00358", "Nguyễn Hoàng Anh", "1942421269", 22_000_000, 253_905, null),
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const money = (v: number | null | undefined) => {
    if (v === null || v === undefined || typeof v !== "number" || Number.isNaN(v)) {
        return <span className="text-slate-400 dark:text-slate-500">--</span>;
    }
    return <>{mixformatCurrency(v, false)}</>;
};

const text = (v: string | null | undefined) =>
    v ? <>{v}</> : <span className="text-slate-400 dark:text-slate-500">--</span>;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function PitDeduction({ data = DATA }: { data?: PitRow[] }) {
    const [isDark, setIsDark] = useState(false);
    const [showModalSettings, setShowModalSettings] = useState(false)

    const columns: ColumnsType<PitRow> = useMemo(
        () => [
            {
                title: "Mã nhân sự",
                dataIndex: "employeeCode",
                key: "employeeCode",
                fixed: "left",
                width: 110,
            },
            {
                title: "Họ và tên",
                dataIndex: "fullName",
                key: "fullName",
                fixed: "left",
                width: 200,
                render: (v) => <span>{v}</span>,
            },
            {
                title: "Mã số thuế",
                dataIndex: "taxCode",
                key: "taxCode",
                width: 130,
                render: text,
            },
            {
                title: "Đối tượng",
                dataIndex: "subject",
                key: "subject",
                width: 110,
                render: text,
            },
            {
                title: "Công ty",
                dataIndex: "company",
                key: "company",
                width: 110,
                render: text,
            },
            {
                title: "Loại hợp đồng",
                dataIndex: "contractType",
                key: "contractType",
                width: 130,
                render: text,
            },
            {
                title: "Thuế suất",
                dataIndex: "taxRate",
                key: "taxRate",
                width: 100,
                render: text,
            },
            {
                title: "Ủy quyền quyết toán thuế",
                dataIndex: "authorized",
                key: "authorized",
                width: 170,
                 align: "center",
                render: (v: boolean) => (v ? "Có" : "Không"),
            },
            {
                title: "Tổng thu nhập chịu thuế",
                dataIndex: "taxableIncome",
                key: "taxableIncome",
                width: 170,
                align: "right",
                render: money,
            },
            {
                title: "Giảm trừ",
                children: [
                    {
                        title: "Số người phụ thuộc",
                        dataIndex: "dependents",
                        key: "dependents",
                        width: 150,
                        align: "right",
                        render: money,
                    },
                    {
                        title: "Mức miễn giảm",
                        dataIndex: "dependentDeduction",
                        key: "dependentDeduction",
                        width: 130,
                        align: "right",
                        render: money,
                    },
                    {
                        title: "Bản thân",
                        dataIndex: "selfDeduction",
                        key: "selfDeduction",
                        width: 130,
                        align: "right",
                        render: money,
                    },
                    {
                        title: "Bảo hiểm",
                        dataIndex: "insuranceDeduction",
                        key: "insuranceDeduction",
                        width: 110,
                        align: "right",
                        render: money,
                    },
                ],
            },
            {
                title: "Tổng giảm trừ",
                dataIndex: "totalDeduction",
                key: "totalDeduction",
                width: 140,
                align: "right",
                render: money,
            },
            {
                title: "Tổng thu nhập tính thuế",
                dataIndex: "finalTaxableIncome",
                key: "finalTaxableIncome",
                width: 170,
                align: "right",
                render: money,
            },
            {
                title: "Thuế TNCN phải nộp",
                dataIndex: "taxPayable",
                key: "taxPayable",
                width: 150,
                align: "right",
                render: money,
            },
            {
                title: "Thuế TNCN đã khấu trừ",
                dataIndex: "taxWithheld",
                key: "taxWithheld",
                width: 160,
                align: "right",
                render: money,
            },
            {
                title: "Thuế TNCN còn phải nộp",
                dataIndex: "taxRemaining",
                key: "taxRemaining",
                width: 170,
                align: "right",
                render: money,
            },
            {
                title: "Thuế TNCN đã nộp thừa",
                dataIndex: "taxOverpaid",
                key: "taxOverpaid",
                width: 160,
                align: "right",
                render: money,
            },
        ],
        []
    );

    return (
        <Layout>
            <Helmet>
                <title>Danh sách thuế TNCN | Khothi</title>
            </Helmet>

             {/* Header */}
            <div className="flex justify-between flex-wrap items-center mb-6 border-b border-gray-200 dark:border-gray-800 pb-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {"Danh sách thuế TNCN"}
                </h2>
            </div>

            {/* --- HEADER ACTIONS --- */}
            <div className="text-sm border-b dark:border-gray-700 px-4 md:px-6 py-3 flex flex-wrap justify-between items-center gap-4 sticky top-[64px] bg-white dark:bg-[#1f1f1f] z-10 shadow-sm mb-4">

                <div className='overflow-x-auto no-scrollbar'>
                    <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                        <button
                            type="button"
                            className={`font-medium pb-1 border-b-2 transition-colors text-[#3e91ab] border-[#3e91ab]`}
                        >
                            Chi tiết
                        </button>
                    </div>
                </div>

                <div className='overflow-x-auto no-scrollbar'>
                    <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max">
                        <button onClick={() => setShowModalSettings(true)} className="flex items-center text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"> <Funnel size={14} className="mr-1.5 text-gray-400" /> Tùy chỉnh</button>
                        <button className="flex items-center text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"> <FolderOutput size={14} className="mr-1.5 text-gray-400" /> Export</button>
                        <button className="flex items-center text-xs text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"> <File size={14} className="mr-1.5 text-gray-400" /> Biểu mẫu</button>

                    </div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <ConfigProvider
                    theme={{
                        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                        components: {
                            Table: {
                                headerBg: isDark ? "#1e293b" : "#f8fafc",
                                headerColor: isDark ? "#cbd5e1" : "#64748b",
                                borderColor: isDark ? "#334155" : "#e5e7eb",
                                rowHoverBg: isDark ? "#1e293b" : "#f8fafc",
                                colorBgContainer: isDark ? "#0f172a" : "#ffffff",
                                cellPaddingBlock: 10,
                            },
                        },
                    }}
                >

                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        bordered
                        size="middle"
                        scroll={{ x: "max-content" }}
                    />
                </ConfigProvider>
            </div>
            <ModalCustomFilter open={showModalSettings} onCancel={() => setShowModalSettings(false)}/>
        </Layout>
    );
}